import type { OsmRoadRecord } from '@/utils/osmRoadCache';

export interface OsmRoadMatch {
  road: OsmRoadRecord;
  tier: number;
}

const normalize = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Tiered name search over the tenant's OSM-sourced roads — exact → prefix
 * → substring, same shape as the cadastral street search.
 *
 * `excludeNames` is the cadastral address index's set of normalized street
 * names: an OSM road that duplicates an official street is skipped here,
 * since the cadastral result already covers it (with house numbers, which
 * OSM data doesn't have) — OSM roads exist in search results to fill gaps,
 * not to duplicate better-covered results.
 */
export function searchOsmRoads(
  roads: OsmRoadRecord[],
  rawQuery: string,
  excludeNames: ReadonlySet<string>,
  limit = 10,
): OsmRoadMatch[] {
  const query = rawQuery.trim();
  if (!query) return [];
  const needle = normalize(query);
  if (!needle) return [];

  const results: OsmRoadMatch[] = [];
  const seen = new Set<number>();
  const push = (road: OsmRoadRecord, tier: number) => {
    if (seen.has(road.id)) return;
    seen.add(road.id);
    results.push({ road, tier });
  };

  const candidates = roads.filter((r) => !excludeNames.has(normalize(r.name)));
  for (const r of candidates) if (normalize(r.name) === needle) push(r, 1);
  for (const r of candidates) if (normalize(r.name).startsWith(needle)) push(r, 2);
  for (const r of candidates) if (normalize(r.name).includes(needle)) push(r, 3);

  results.sort((a, b) => a.tier - b.tier || a.road.name.localeCompare(b.road.name));
  return results.slice(0, limit);
}
